// The console's measurement system — metric by default, imperial by choice.
//
// The preference converts DISPLAY ONLY. Limits, forecasts, bathymetry and the
// whole API stay metric; every conversion happens at the moment a number is
// formatted for a human, so no threshold, comparison or stored value ever moves
// with the toggle. What converts: wave and swell heights, depths and elevations,
// vessel dimensions, temperature, and the primer's kilometre distances. What
// deliberately does not: wind and current in knots and visibility/distances in
// nautical miles (the marine convention in both systems), wave period in
// seconds, and the SVP workspace end to end (its depths pair with the m/s sound
// speeds and the SI CSV the survey software ingests).
//
// The mode is a preference (ui/prefs.ts): localStorage always, mirrored to
// users.prefs when signed in — the same one-key pattern as ui/time.ts.

import { getPref, setPref, subscribePref } from "./prefs.js";

export type UnitSystem = "metric" | "imperial";

const PREF_KEY = "units";

const FT_PER_M = 1 / 0.3048;
const MI_PER_KM = 1 / 1.609344;

/** The active system. Anything unrecognised in the pref blob is "metric" — the
 *  default, and the console's behavior before the preference existed. */
export function getUnitSystem(): UnitSystem {
  return getPref(PREF_KEY) === "imperial" ? "imperial" : "metric";
}

export function setUnitSystem(system: UnitSystem): void {
  setPref(PREF_KEY, system);
}

/** Subscribe to system flips. Returns the unsubscribe. */
export function subscribeUnits(fn: () => void): () => void {
  return subscribePref(PREF_KEY, () => fn());
}

function imperial(): boolean {
  return getUnitSystem() === "imperial";
}

// ---------------------------------------------------------------------------
// Lengths — wave heights, depths, elevations, vessel dimensions all share the
// metre↔foot pair. Conversion is one linear scale, so it is equally valid for
// differences (a model-spread ±0.2 m is ±0.7 ft).

// The `…In(value, system)` variants name their system instead of reading the
// preference. They exist for the printable report, which can be asked to print
// BOTH systems — "2.1 m (6.9 ft)" — and so needs to format a number in a system
// that is not the console's current one, without reaching in and flipping the
// preference to do it. The preference-reading functions below delegate to these,
// so there is still one conversion in the codebase and not two.

/** The display unit for lengths and heights, in a named system. */
export function lenUnitOf(system: UnitSystem): "m" | "ft" {
  return system === "imperial" ? "ft" : "m";
}

/** Metres → the display unit of a named system. */
export function convLenIn(m: number, system: UnitSystem): number {
  return system === "imperial" ? m * FT_PER_M : m;
}

/** fmtLen in a named system. */
export function fmtLenIn(m: number, system: UnitSystem): string {
  return system === "imperial" ? `${Math.round(m * FT_PER_M)} ft` : `${m} m`;
}

/** fmtLimitLen in a named system. */
export function fmtLimitLenIn(m: number, system: UnitSystem): string {
  return system === "imperial" ? `${trim((m * FT_PER_M).toFixed(1))} ft` : `${m} m`;
}

/** fmtDepth in a named system. */
export function fmtDepthIn(m: number, system: UnitSystem): string {
  return `${Math.round(convLenIn(m, system)).toLocaleString()} ${lenUnitOf(system)}`;
}

/** The display unit for lengths and heights. */
export function lenUnit(): "m" | "ft" {
  return lenUnitOf(getUnitSystem());
}

/** The unit written out, for subtitles ("metres · 7-day"). */
export function lenWord(): "metres" | "feet" {
  return imperial() ? "feet" : "metres";
}

/** Metres → the display unit. Identity in metric mode. */
export function convLen(m: number): number {
  return convLenIn(m, getUnitSystem());
}

/** Convert a chart series of metres for display. Metric mode returns the same
 *  array untouched — zero cost on the default path. */
export function convLenSeries(data: (number | null)[]): (number | null)[] {
  if (!imperial()) return data;
  return data.map((v) => (v == null || !Number.isFinite(v) ? v : v * FT_PER_M));
}

/** A depth or elevation: whole units, thousands grouped — "1,234 m" / "4,049 ft". */
export function fmtDepth(m: number): string {
  return fmtDepthIn(m, getUnitSystem());
}

/** A length as prose or a roster states it: the stored number verbatim in
 *  metric (exactly what the console printed before the preference existed),
 *  whole feet in imperial — "25.6 m" / "84 ft". */
export function fmtLen(m: number): string {
  return fmtLenIn(m, getUnitSystem());
}

/** A limit or draft, where tenths matter: the stored number verbatim in metric,
 *  one decimal (trailing zero trimmed) in imperial — "2.5 m" / "8.2 ft". */
export function fmtLimitLen(m: number): string {
  return fmtLimitLenIn(m, getUnitSystem());
}

// ---------------------------------------------------------------------------
// Temperature — absolute readings only (the drifter SST). The SVP workspace
// keeps °C with the rest of its SI profile.

export function tempUnitOf(system: UnitSystem): "°C" | "°F" {
  return system === "imperial" ? "°F" : "°C";
}

/** °C → an absolute temperature in a named system. */
export function convTempIn(c: number, system: UnitSystem): number {
  return system === "imperial" ? c * 1.8 + 32 : c;
}

export function tempUnit(): "°C" | "°F" {
  return tempUnitOf(getUnitSystem());
}

/** °C → the display unit, as an absolute temperature. */
export function convTemp(c: number): number {
  return convTempIn(c, getUnitSystem());
}

// ---------------------------------------------------------------------------
// Distances — the primer's kilometre radii. Nautical miles never convert.

/** A stated kilometre distance: "50 km" / "31 mi". */
export function fmtKm(km: number): string {
  return imperial() ? `${Math.round(km * MI_PER_KM)} mi` : `${km} km`;
}

// ---------------------------------------------------------------------------
// The editor codec — the Vessels page renders stored metric values into text
// fields and parses what comes back. The invariant: open + save without
// touching a value must return the EXACT stored number, never a converted
// round-trip of it. editLenResolve enforces that by string identity — a field
// still reading exactly what we rendered resolves to the original, and only a
// genuinely edited string is parsed and converted.

function trim(s: string): string {
  return s.replace(/\.?0+$/, "");
}

/** Render a stored metric length for an edit field: up to two decimals in
 *  metric (the page's existing fmtM convention), one decimal in imperial. */
export function editLenStr(m: number): string {
  return imperial() ? trim((m * FT_PER_M).toFixed(1)) : trim(m.toFixed(2));
}

/** Parse an edit-field string back to metres. Metric input passes through
 *  untouched; imperial input converts and rounds to centimetres, so a value
 *  saved from feet re-renders and re-saves to itself (a stable fixed point).
 *  Non-numeric input returns NaN for the caller's validation to reject. */
export function editLenParse(raw: string): number {
  const v = Number(raw);
  if (!Number.isFinite(v)) return NaN;
  return imperial() ? Math.round(v * 0.3048 * 100) / 100 : v;
}

/** Resolve an edit field to metres: the exact original while the text still
 *  reads as rendered, the parsed conversion once it was really edited. */
export function editLenResolve(raw: string, rendered: string, originalM: number): number {
  return raw.trim() === rendered ? originalM : editLenParse(raw);
}
